<template>
  <BCard class="px-1">
    <div class="text-black text-2xl font-semibold mb-2">
      Mode Kerja
    </div>
    <div class="p-1 rounded-lg border mb-2">
      <p class="text-black text-xl font-medium mb-1">
        Mode Kerja Default per Role
      </p>
      <p>Mode kerja ini menjadi default jika tidak ada shifting.</p>
      <div class="grid grid-cols-3 gap-[1rem] items-start">
        <RoleCard
          v-for="(role, index) in roles"
          :ref="`role-card-${index}`"
          :key="index"
          :role="role"
          @on-change="handleOpenModalConfirmRoleCard(index)"
          @on-after-submit="response => handleAfterSubmitRoleCard({ response, index })"
        />
      </div>
    </div>
    <div class="p-1 rounded-lg border">
      <div class="flex justify-between items-start border-b pb-[.5rem] mb-2">
        <div>
          <p class="text-black text-xl font-medium mb-1">
            Shifting - Manajemen
          </p>
          <p>Atur jadwal kerja per hari. Jika diaktifkan, jadwal ini akan mengoverride mode kerja default.</p>
        </div>
        <BButton
          class="row-span-2"
          variant="primary"
          @click="handleOpenShiftingDrawer({ source: 'shifting-division-btn' })"
        >
          <div class="flex items-center gap-[.25rem]">
            <span class="k-setting-2 text-[20px]" />
            <span>Shifting Divisi</span>
          </div>
        </BButton>
      </div>
      <div>
        <!-- Tools -->
        <div class="flex items-center gap-[1rem] mb-[1rem]">
          <div
            class="flex items-center !w-[300px] rounded-[8px] gap-[5px] px-[8px]  border-[1px] border-[#E2E2E2] h-[40px]"
          >
            <span
              class="k-search-normal-1 font-bold align-middle"
              style="font-size: 20px"
            />
            <BFormInput
              v-model="filterTable.keyword"
              placeholder="Cari nama..."
              class="border-0 grow"
              @input="onSearchUserWorkConfig"
            />
          </div>
          <v-select
            v-model="filterTable.division"
            label="division_name"
            :reduce="option => option"
            :options="divisions"
            placeholder="Pilih Divisi"
            class="min-w-[300px]"
            @input="validateParams"
          />

          <BButton
            v-b-tooltip.hover.html.topleft="{
              title: resetButtonTooltip,
              variant: 'primary',
              customClass: 'double-text-tooltip'
            }"
            type="button"
            :variant="filterTable.selectedItems.length > 0 ? 'outline-primary' : 'outline-secondary'"
            :disabled="filterTable.selectedItems.length < 1"
            class="ms-auto"
            @click="handleOpenModalConfirmResetUserConfig"
          >
            Reset
          </BButton>
        </div>
        <!-- Table -->
        <Table
          ref="employee-table"
          :items="items"
          :filter="filterTable"
          :loading="isLoadingTable"
          @scroll="onScrollTable"
          @selection-change="itemsObject => filterTable.selectedItems = Object.keys(itemsObject).map(Number)"
          @edit-work-config="item => handleOpenShiftingDrawer({ source: 'table-action-btn', data: item })"
        />
      </div>
    </div>

    <ShiftingDrawer
      :drawer="shiftingDrawer"
      :divisions="divisions"
      @on-submit="handleOpenShiftingModalConfirmation"
      @on-close="handleCloseShiftingDrawer"
    />

    <ModalConfirmation :modal="modalConfirm" />
    <ModalAlert
      :modal="modalAlert"
      @on-close="handleResetModalAlert"
    />
  </BCard>
</template>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
</style>

<script>
import { komtimAxiosIns } from '@/libs/axios'
import vSelect from 'vue-select'
import ModalAlert from '../components/ModalAlert.vue'
import ModalConfirmation from '../components/ModalConfirmation.vue'
import RoleCard from './RoleCard.vue'
import ShiftingDrawer from './ShiftingDrawer.vue'
import { confirmModalMessage, defaultManagementWorkModeCard } from '../helpers/string-html'
import Table from './Table.vue'
import { resetButtonTooltip } from './constants'

export default {
  components: {
    RoleCard,
    ModalConfirmation,
    ModalAlert,
    ShiftingDrawer,
    vSelect,
    Table,
  },
  data() {
    return {
      resetButtonTooltip,
      divisions: [],
      roles: [],
      selectedRoleIndex: -1,
      modalConfirm: {
        id: 'work-mode-modal-confirmation',
        title: '',
        description: '',
        negativeButtonAction: () => {},
        negativeButtonText: '',
        positiveButtonAction: () => {},
        positiveButtonText: '',
        closeButtonAction: () => {},
      },
      modalAlert: {
        id: 'work-mode-modal-alert',
        title: '',
        description: '',
        status: '',
      },
      shiftingDrawer: {
        id: 'shifting-drawer',
        visible: false,
        title: '',
        triggerSource: '',
        isLoading: false,
        data: {},
      },
      shiftingDrawerSubmitionData: {},
      filterTable: {
        division: null,
        keyword: '',
        selectedItems: [],
      },
      isLoadingTable: false,
      limit: 10,
      offset: 0,
      isLastData: false,
      items: [],
    }
  },
  computed: {
    /**
     * Selected role by modal
     * @returns {Object}
     */
    selectedRole() {
      return this.roles[this.selectedRoleIndex] || {}
    },
    /**
     * Check if the drawer is triggered by division shifting
     * @returns {Boolean}
     */
    isDrawerTriggeredByDivisionShiftingButton() {
      return this.shiftingDrawer.triggerSource === 'shifting-division-btn'
    },
    /**
     * Check if the drawer is triggered by table action button
     * @returns {Boolean}
     */
    isDrawerTriggeredByTableActionButton() {
      return this.shiftingDrawer.triggerSource === 'table-action-btn'
    },
    /**
     * Default management work mode
     * @returns {Object}
     */
    defaultManagementWorkMode() {
      return this.roles?.find(role => role.title === 'Manajemen') || {}
    },
  },
  created() {
    this.getWorkModeConfigs()
    this.getDivisions()
  },
  beforeDestroy() {
    this.resetTable()
  },
  methods: {
    /**
     * Handle get work configs
     */
    async getWorkModeConfigs() {
      const url = 'v1/work_configurations'
      try {
        const { data: { data } } = await komtimAxiosIns.get(url)
        const styles = [
          {
            icon: 'k-people text-[#10673E]',
            bgIcon: 'bg-[#BFF8DD]',
          },
          {
            icon: 'k-profile-2user text-[#AB2900]',
            bgIcon: 'bg-[#FFECE9]',
          },
          {
            icon: 'k-user text-[#5B3299]',
            bgIcon: 'bg-[#E2D8F1]',
          },
        ]

        this.roles = data.map((role, index) => {
          const { icon, bgIcon } = styles[index]
          return {
            id: role.id,
            title: role.position_name,
            icon,
            bgIcon,
            data: {
              work_mode: role.work_mode,
              start_clock: role.check_in_time,
              end_clock: role.check_out_time,
            },
            isShownSaveButton: false,
            triggerSource: '',
          }
        })
      } catch (error) {
        console.warn(error)
      }
    },
    /**
     * Handle opem modal
     * @param {Number} index - roles array index
     */
    handleOpenModalConfirmRoleCard(index) {
      this.selectedRoleIndex = index

      this.modalConfirm.title = 'Ubah Mode Kerja?'
      this.modalConfirm.description = `${confirmModalMessage('Perubahan mode kerja akan memengaruhi jadwal shifting. Pilih bagaimana Anda ingin menerapkan perubahan ini')}`
      this.modalConfirm.negativeButtonAction = () => this.handleSubmitRoleCard('negative-btn')
      this.modalConfirm.negativeButtonText = 'Samakan dengan Mode Kerja'
      this.modalConfirm.positiveButtonAction = () => this.handleSubmitRoleCard('positive-btn')
      this.modalConfirm.positiveButtonText = 'Pertahankan Jadwal Shifting'
      this.modalConfirm.closeButtonAction = this.handleResetRoleCard

      this.$bvModal.show(this.modalConfirm.id)
    },
    /**
     * Handle modal confirm action
     * @param {String} triggerSource - trigger source from modal ('negative-btn' | 'positive-btn')
     */
    handleSubmitRoleCard(triggerSource) {
      const role = this.roles[this.selectedRoleIndex]
      role.isShownSaveButton = true
      role.triggerSource = triggerSource
      this.$bvModal.hide(this.modalConfirm.id)
    },
    /**
     * Handle after submit action
     * @param {Object} - response from RoleCard and roles array index
     */
    handleAfterSubmitRoleCard({ response, index }) {
      if (response.status === 'success') {
        this.modalAlert.title = 'Perubahan berhasil disimpan'
        const description = {
          kept: 'Mode kerja & shifting berhasil disinkronkan.',
          synced: 'Mode kerja berhasil diperbarui. Shifting kini mengikuti mode kerja terbaru.',
          none: 'Mode kerja berhasil diperbarui.',
        }
        this.modalAlert.description = description[response.schedule_status]
        this.modalAlert.status = response.status

        const role = this.roles[index]
        role.isShownSaveButton = false
        role.triggerSource = ''
        this.selectedRoleIndex = -1
      } else if (response.status === 'fail') {
        this.modalAlert.title = 'Gagal menyimpan perubahan'
        this.modalAlert.description = 'Terjadi kesalahan. Silakan coba lagi.'
        this.modalAlert.status = response.status
      }

      this.$bvModal.show(this.modalAlert.id)
    },
    /**
     * Handle reset role card form value to default
     */
    handleResetRoleCard() {
      this.roles[this.selectedRoleIndex].isShownSaveButton = false
      this.$refs[`role-card-${this.selectedRoleIndex}`][0].resetForm()
      this.selectedRoleIndex = -1
      this.$bvModal.hide(this.modalConfirm.id)
    },
    /**
     * Handle reset modal alert
     */
    handleResetModalAlert() {
      this.modalAlert.title = ''
      this.modalAlert.description = ''
      this.modalAlert.status = ''
    },
    /**
     * Fetch divisions resource
     */
    async getDivisions() {
      const params = 'status=true'
      const url = `v1/divisions/resource?${params}`
      try {
        const { data: { data } } = await komtimAxiosIns.get(url)
        this.divisions = data
      } catch (error) {
        console.warn(error)
      }
    },
    /**
     * Handle open shifting drawer
     * @param {Object} - trigger source for drawer ('table-action-btn' | 'shifting-division-btn')
     */
    handleOpenShiftingDrawer({ source, data = {} }) {
      this.shiftingDrawer.title = source === 'table-action-btn' ? 'Pengaturan Shifting Karyawan' : 'Pengaturan Shifting Divisi'
      this.shiftingDrawer.triggerSource = source
      this.shiftingDrawer.data = data

      this.shiftingDrawer.visible = true
    },
    /**
     * Handle close shifting drawer
     */
    handleCloseShiftingDrawer() {
      this.shiftingDrawer.visible = false
      this.shiftingDrawer.data = {}
    },
    /**
     * Handle open modal confirmation
     * @param {Object} - oayload & division for schedules config
     */
    async handleOpenShiftingModalConfirmation({ payload, division }) {
      this.shiftingDrawerSubmitionData = { payload, division }
      if (!division?.id && this.isDrawerTriggeredByTableActionButton) {
        await this.handleSubmitSchedulesConfig()
        this.validateParams()
        return
      }

      this.modalConfirm.title = 'Simpan Perubahan Pengaturan?'
      const firstMsg = `Perubahan pengaturan shifting untuk divisi ${division?.division_name} akan diterapkan ke semua anggota dalam divisi ini.`
      this.modalConfirm.description = `
        ${confirmModalMessage(firstMsg)}
        ${confirmModalMessage('Pastikan jadwal sudah sesuai sebelum melanjutkan.')}
      `
      this.modalConfirm.negativeButtonAction = this.handleResetShiftingDrawerData
      this.modalConfirm.negativeButtonText = 'Batal'
      this.modalConfirm.positiveButtonAction = this.handleSubmitSchedulesConfig
      this.modalConfirm.positiveButtonText = 'Ya, Simpan'
      this.modalConfirm.closeButtonAction = this.handleResetShiftingDrawerData

      this.$bvModal.show(this.modalConfirm.id)
    },
    /**
     * Handle submit schedules config
     */
    async handleSubmitSchedulesConfig() {
      try {
        this.$bvModal.hide(this.modalConfirm.id)
        this.shiftingDrawer.isLoading = true
        const { payload, division } = this.shiftingDrawerSubmitionData

        const shiftByUserUrl = `/v1/user_work_configurations/user/${this.shiftingDrawer.data.user_id}`
        const shiftByDivisionUrl = `/v1/user_work_configurations/division/${division?.id}`
        const url = division?.id && this.isDrawerTriggeredByDivisionShiftingButton ? shiftByDivisionUrl : shiftByUserUrl

        await komtimAxiosIns.put(url, payload)

        this.modalAlert.title = 'Pengaturan Berhasil Disimpan'
        if (this.isDrawerTriggeredByDivisionShiftingButton) {
          this.modalAlert.description = `Pengaturan shifting untuk divisi ${division.division_name} berhasil diperbarui dan sudah diterapkan ke seluruh anggota.`
        } else {
          this.modalAlert.description = 'Shifting karyawan berhasil diperbarui.'
        }
        this.modalAlert.status = 'success'

        this.shiftingDrawer.visible = false
      } catch (error) {
        this.modalAlert.title = 'Gagal Menyimpan Pengaturan'
        this.modalAlert.description = 'Terjadi kesalahan saat menyimpan pengaturan. Silakan coba lagi.'
        this.modalAlert.status = 'fail'
      } finally {
        this.shiftingDrawer.isLoading = false
        this.$bvModal.show(this.modalAlert.id)
      }
    },
    handleResetShiftingDrawerData() {
      this.shiftingDrawerSubmitionData = {}
      this.$bvModal.hide(this.modalConfirm.id)
    },
    /**
     * Fetch users work configuration with infinite scroll and keyword filter.
     */
    async getUsersWorkConfigByDivision() {
      try {
        this.isLoadingTable = true
        const params = {
          limit: this.limit,
          offset: this.offset,
          search: this.filterTable.keyword,
          division_id: this.filterTable.division.id,
        }
        const url = '/v1/user_work_configurations'

        const { data: { data } } = await komtimAxiosIns.get(url, { params })
        if (data.length < this.limit) {
          this.isLastData = true
        } else {
          this.isLastData = false
        }

        this.items.push(...data)
        this.offset += data.length
      } catch (error) {
        this.items = []
        console.warn(error)
      } finally {
        this.isLoadingTable = false
      }
    },
    /**
     * Debounced search handler.
     * Reload data based on current keyword.
     */
    onSearchUserWorkConfig: _.debounce(async function search() {
      this.offset = 0
      this.isLastData = false
      this.items = []
      await this.getUsersWorkConfigByDivision()
    }, 1000),
    /**
     * Validate stored search params.
     * Restore previous search keyword or load default data.
     */
    validateParams() {
      if (this.filterTable.keyword !== '') {
        this.onSearchUserWorkConfig()
        return
      }
      this.items = []
      this.offset = 0
      this.getUsersWorkConfigByDivision()
    },
    /**
     * Handle infinite scroll event.
     * Load more data when reaching near bottom of table.
     */
    onScrollTable(event) {
      const table = event.target || document.querySelector('#work-configs-table')
      const isScrollEnd = table.scrollTop >= table.scrollHeight - table.offsetHeight - 20
      if (isScrollEnd && !this.isLastData && !this.isLoadingTable) this.getUsersWorkConfigByDivision()
    },
    /**
     * Reset table state including  search keyword.
     */
    resetTable() {
      this.offset = 0
      this.filterTable.keyword = ''
      this.filterTable.division = null
      this.filterTable.selectedItems = []
      this.items = []
    },
    /**
     * Handle open modal confirmation before reset user's config
     */
    handleOpenModalConfirmResetUserConfig() {
      this.modalConfirm.title = 'Kembalikan ke Mode Kerja Default?'
      this.modalConfirm.description = `
        ${confirmModalMessage('User ini memiliki jadwal shifting yang mengoverride mode kerja default.')}
        ${confirmModalMessage('Jika dilanjutkan, jadwal shifting akan dihapus dan user akan mengikuti pengaturan mode kerja default.')}
        ${defaultManagementWorkModeCard(this.defaultManagementWorkMode.data)}
      `
      this.modalConfirm.negativeButtonAction = this.handleResetUsersConfig
      this.modalConfirm.negativeButtonText = 'Reset ke Default'
      this.modalConfirm.positiveButtonAction = () => this.$bvModal.hide(this.modalConfirm.id)
      this.modalConfirm.positiveButtonText = 'Batal'
      this.modalConfirm.closeButtonAction = () => this.$bvModal.hide(this.modalConfirm.id)

      this.$bvModal.show(this.modalConfirm.id)
    },
    /**
     * Handle reset users config
     */
    async handleResetUsersConfig() {
      try {
        this.$bvModal.hide(this.modalConfirm.id)
        const payload = {
          user_ids: this.filterTable.selectedItems,
        }
        const url = '/v1/user_work_configurations/reset'
        await komtimAxiosIns.post(url, payload)

        this.modalAlert.title = 'Shifting berhasil dihapus'
        this.modalAlert.description = 'Jadwal kini mengikuti mode kerja default'
        this.modalAlert.status = 'success'
        this.filterTable.selectedItems = []
        this.validateParams()
      } catch (error) {
        this.modalAlert.title = 'Gagal mengembalikan ke default'
        this.modalAlert.description = 'Terjadi kesalahan. Silakan coba lagi'
        this.modalAlert.status = 'fail'
      }

      this.$bvModal.show(this.modalAlert.id)
    },
  },
}
</script>
